import React, { useEffect, useRef, useState } from "react";
import { Box, TablePagination } from "@mui/material";
import CardComponent from "./CardComponent";
import MachineTable from "./MachineTable";
import { getOwnMachines } from "../../../Services/authService";
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../Context/ThemeContext';
import { DARKCOLORS, LIGHTCOLORS } from '../../../Utils/constants';



const InternalMachine = ({ searchTerm, filterTerm, viewMode = 'card' }) => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const { t } = useTranslation();
  const { mode } = useThemeContext();

  const paginationTextColor = mode === 'dark' ? DARKCOLORS.HeadingLightBackground : LIGHTCOLORS.HeadingLightBackground;

  // Tracks the last fetched search/filter so a change restarts from page 1
  const prevFilters = useRef({ searchTerm, filterTerm });

  useEffect(() => {
    const filtersChanged =
      prevFilters.current.searchTerm !== searchTerm ||
      prevFilters.current.filterTerm !== filterTerm;
    if (filtersChanged) {
      prevFilters.current = { searchTerm, filterTerm };
      if (page !== 0) {
        // Search/filter results always start from the first page; skip the
        // stale-page fetch so it can't overwrite the page-1 results
        setPage(0);
        return;
      }
    }

    const fetchMachines = async () => {
      try {
        setLoading(true);
        const data = await getOwnMachines("location", searchTerm, filterTerm, "", rowsPerPage, page);
        const machinesList = data.content || [];
        
        // Map machineType from API response into vendingMachine structure expected by components
        const transformedMachines = machinesList.map((machine) => ({
          ...machine,
          vendingMachine: {
            machineType: machine.machineType || "",
          },
        }));
        
        // Merge demo machines with API-fetched machines, avoiding duplicates by ID
        const existingIds = new Set(transformedMachines.map(m => m.id));
      
        setMachines([ ...transformedMachines]);
        setTotalElements(data.totalElements || 0);
      } catch (err) {
        setError(err.message || "Failed to fetch machines");
      } finally {
        setLoading(false);
      }
    };

    fetchMachines();
  }, [searchTerm, filterTerm, page, rowsPerPage]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) return (
    <Box sx={{ width: '100%', display: 'flex', margin: '20px', justifyContent: 'center', alignSelf: 'center', textAlign: 'center' }}>
      <p>{t('vendingMachines.Loading')}</p>
    </Box>
  );
  
  if (error) return (
    <Box sx={{ width: '100%', display: 'flex', margin: '20px', justifyContent: 'center', alignSelf: 'center', textAlign: 'center' }}>
      <p>{error}</p>
    </Box>
  );

  if (machines.length === 0) return (
    <Box
      sx={{
        height: '300px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '18px',
        textAlign: 'center',
        color: mode === 'dark' ? DARKCOLORS.GreyScale40 : LIGHTCOLORS.GreyScale40,
      }}
    >
      {t('vendingMachines.noResultsFound')}
    </Box>
  );

  return (
    <>
      {viewMode === 'card' ? (
        <Box
          className='franchise-grid'
          sx={{
            marginBottom: '50px',
            marginTop: '25px',
            display: 'grid',
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
              xxl: "repeat(4, 1fr)",
            },
            gap: '12px',
          }}
        >
          {machines.map((machine) => (
            <CardComponent key={machine.id} data={machine} />
          ))}
        </Box>
      ) : (
        <MachineTable machines={machines} />
      )}

      {/* TablePagination Controls */}
      
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[ 10, 25, 50, 100]}
          labelRowsPerPage={t('vendingMachines.rowsPerPage') || 'Machines per page:'}
          sx={{
            color: paginationTextColor,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20px',
            marginBottom: '20px',
            '& .MuiTablePagination-selectLabel': {
              color: paginationTextColor,
            },
            '& .MuiTablePagination-displayedRows': {
              color: paginationTextColor,
            },
            '& .MuiTablePagination-select': {
              color: paginationTextColor,
            },
            '& .MuiSvgIcon-root': {
              color: paginationTextColor,
            },
          }}
        />

    </>
  );
};

export default InternalMachine;
